import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Calendar, Clock, Package, Users, User, X, Check, CreditCard, Mail, Phone, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TimeSlotSelector from '../components/TimeSlotSelector';
import DatePicker from '../components/DateRangePicker';
import ServiceCalendarPicker from '../components/ServiceCalendarPicker';
import SharePaymentLinkModal from '../components/SharePaymentLinkModal';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../utils/api';
import { BACKEND_URL } from '../config/api';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const AddBookingWizard = ({ assets, teamMembers, roles, onComplete, onCancel }) => {
  return (
    <Elements stripe={stripePromise}>
      <AddBookingWizardForm 
        assets={assets} 
        teamMembers={teamMembers} 
        roles={roles} 
        onComplete={onComplete} 
        onCancel={onCancel} 
      />
    </Elements>
  );
};

const AddBookingWizardForm = ({ assets, teamMembers, roles, onComplete, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState(null);
  
  // NEW: Load services
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loadingServices, setLoadingServices] = useState(false);
  
  // Get organization timezone from settings (source of truth)
  const orgTimezone = localStorage.getItem('orgTimezone') || 'America/Chicago';
  
  const getTimezoneAbbreviation = (tz) => {
    const abbr = {
      'America/Chicago': 'CST',
      'America/New_York': 'EST',
      'America/Denver': 'MST',
      'America/Los_Angeles': 'PST',
      'America/Phoenix': 'MST',
      'America/Anchorage': 'AKST',
      'Pacific/Honolulu': 'HST'
    };
    return abbr[tz] || 'CST';
  };
  
  const [formData, setFormData] = useState({
    serviceId: '',  // NEW: Required field
    serviceName: '',  // NEW: For display
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '11:00',
    duration: 120,
    groupSize: 1,
    selectedDurationIndex: null,
    selectedAddOns: {}, // {addonId: price}
    chargeAmount: '', // Editable amount for partial payments
    assetId: '',
    teamAssignments: [],
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    pickupLocation: '',
    paymentStatus: '', // No default - user must select
    totalAmount: 0,
    paymentMethod: '',
    timezone: orgTimezone,
    contactInfoConfirmed: false,  // NEW: Confirmation checkbox
    // Stripe card details
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: ''
  });

  const totalSteps = 6; // Updated: 1=Service, 2=Date/Time/Capacity, 3=Add-ons, 4=Customer, 5=Review, 6=Payment
  
  // Entry point selection
  const [entryPoint, setEntryPoint] = useState('service'); // 'service' or 'date'
  
  // Load services on mount
  useEffect(() => {
    loadServices();
  }, []);
  
  // Track expanded descriptions
  const [expandedServices, setExpandedServices] = useState({});
  
  // Track if we're intentionally selecting a service (not navigating back)
  const [isSelectingService, setIsSelectingService] = useState(false);
  
  // Track if we just selected a time (for auto-advance)
  const [justSelectedTime, setJustSelectedTime] = useState(false);
  
  // Track sub-view within step 2 (calendar → time slots with inline capacity)
  const [step2SubView, setStep2SubView] = useState('calendar'); // 'calendar', 'timeSlots'
  
  // Auto-advance to step 2 when service is selected in step 1
  useEffect(() => {
    if (currentStep === 1 && formData.serviceId && entryPoint === 'service' && isSelectingService) {
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        setCurrentStep(2);
        setStep2SubView('calendar'); // Reset to calendar view
        setIsSelectingService(false); // Reset flag
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.serviceId, currentStep, entryPoint, isSelectingService]);
  
  // Calculate dynamic progress within Step 2
  const getStep2Progress = () => {
    if (currentStep !== 2) return currentStep;
    // Step 2 has 2 sub-views, advance progress as user makes selections
    if (step2SubView === 'calendar') return 2.0;
    if (step2SubView === 'timeSlots' && !formData.startTime) return 2.25;
    if (step2SubView === 'timeSlots' && formData.startTime && !formData.groupSize) return 2.5;
    if (step2SubView === 'timeSlots' && formData.startTime && formData.groupSize) return 2.75;
    return 2;
  };
  
  const toggleServiceDescription = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };
  
  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const response = await api.get('/services');
      // Backend returns array directly, not nested in .services
      setServices(response.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoadingServices(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        // Step 1: Service selection
        if (!formData.serviceId) {
          toast.error('Please select a service');
          return false;
        }
        return true;
      case 2:
        // Step 2: Date, time, and capacity
        if (!formData.date || !formData.startTime || !formData.endTime) {
          toast.error('Please select date and time');
          return false;
        }
        if (!formData.groupSize || formData.groupSize < 1) {
          toast.error('Please select number of guests/tickets');
          return false;
        }
        return true;
      case 3:
        // Step 3: Add-ons (optional, always valid)
        return true;
      case 4:
        // Step 4: Customer info
        if (!formData.customerName || !formData.customerName.trim()) {
          toast.error('Customer name is required');
          return false;
        }
        if (!formData.customerEmail || !formData.customerEmail.trim()) {
          toast.error('Customer email is required');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.customerEmail)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        if (!formData.customerPhone || !formData.customerPhone.trim()) {
          toast.error('Customer phone is required');
          return false;
        }
        return true;
      case 5:
        // Step 5: Review - no validation needed, payment selection optional
        return true;
      case 6:
        // Step 6: Payment - must select payment status
        if (!formData.paymentStatus) {
          toast.error('Please select a payment option');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    console.log('=== HANDLE SUBMIT CALLED ===');
    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!formData.startTime || !formData.endTime) {
        toast.error('Missing time information');
        setIsSubmitting(false);
        return;
      }
      
      // Calculate duration
      const [startH, startM] = formData.startTime.split(':').map(Number);
      const [endH, endM] = formData.endTime.split(':').map(Number);
      const duration = (endH * 60 + endM) - (startH * 60 + startM);
      
      // Handle payment based on selected option
      const addOnsArray = Object.entries(formData.selectedAddOns).map(([id, price]) => ({
        id,
        price
      }));
      const addOnsTotal = Object.values(formData.selectedAddOns).reduce((sum, price) => sum + price, 0);

      const bookingData = {
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        servicePrice: selectedService?.price || 0,
        preferredDate: formData.date,
        preferredTime: formData.startTime,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        partySize: formData.groupSize,
        emergencyContactNumber: formData.customerPhone,
        pickupLocation: formData.pickupLocation || '',
        selectedAddOns: addOnsArray,
        addOnsTotal: addOnsTotal,
        paymentStatus: formData.paymentStatus,
        isInternalBooking: true
      };

      // For "Pay Now" or "Share Payment Link", create Stripe Checkout session
      if (formData.paymentStatus === 'pay_now_redirect' || formData.paymentStatus === 'send_link') {
        // Calculate pricing for modal
        const base_price = parseFloat(selectedService?.price || 0);
        const add_ons_total = addOnsTotal;
        const pre_total = base_price + add_ons_total;
        const platform_fee = (pre_total * 0.059) + 0.30;
        const sales_tax = selectedService?.salesTaxEnabled ? pre_total * (selectedService?.salesTaxRate || 0) : 0;
        
        const response = await api.post('/admin/bookings/create-with-checkout', bookingData);
        
        if (response.data.success) {
          const checkoutUrl = response.data.checkoutUrl;
          
          if (formData.paymentStatus === 'pay_now_redirect') {
            // Redirect admin to Stripe to enter card
            toast.success('Opening Stripe payment page...');
            window.open(checkoutUrl, '_blank');
            // Close wizard
            onComplete();
          } else if (formData.paymentStatus === 'send_link') {
            // Show share link modal
            setPaymentLinkData({
              checkoutUrl: checkoutUrl,
              customerEmail: formData.customerEmail,
              customerPhone: formData.customerPhone,
              bookingDetails: {
                serviceName: formData.serviceName,
                preferredDate: formData.date,
                preferredTime: formData.startTime,
                customerName: formData.customerName,
                totalAmount: (pre_total + platform_fee + sales_tax).toFixed(2)
              }
            });
            setShowShareLinkModal(true);
          }
          return;
        } else {
          throw new Error(response.data.message || 'Failed to create payment link');
        }
      }
      
      // For "Already Paid" or "Will Pay Later"
      if (formData.paymentStatus === 'paid' || formData.paymentStatus === 'unpaid') {
        const response = await api.post('/admin/bookings/create', bookingData);
        
        if (response.data.success) {
          toast.success('Booking created successfully!');
          setCreatedBooking({
            ...bookingData,
            ...response.data,
            confirmationCode: response.data.confirmationCode
          });
          setShowSuccessModal(true);
          return;
        } else {
          throw new Error(response.data.message || 'Booking creation failed');
        }
      }
      
      toast.error('Please select a payment option');
      setIsSubmitting(false);
    } catch (error) {
      console.error('=== ERROR IN HANDLE SUBMIT ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create booking';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // NEW: Service Selection Step with TWO entry points
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Start Your Booking
            </h3>
            
            {/* Entry Point Selector - Service First or Date First */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setEntryPoint('service')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  entryPoint === 'service'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Package className="w-4 h-4 inline mr-2" />
                Service First
              </button>
              <button
                onClick={() => setEntryPoint('date')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  entryPoint === 'date'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Date First
              </button>
            </div>
            
            {/* Service First Flow */}
            {entryPoint === 'service' && (
              <>
                {loadingServices ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                <p className="text-gray-500 mt-2">Loading services...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600 font-medium">No services available</p>
                <p className="text-sm text-gray-500 mt-1">Create a service first</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {services.filter(s => s.active).map((service) => {
                  const isExpanded = expandedServices[service.id] || false;
                  const shouldTruncate = service.description && service.description.length > 100;
                  const displayDescription = shouldTruncate && !isExpanded 
                    ? service.description.substring(0, 100) + '...' 
                    : service.description;
                  
                  // Get main image from service
                  const mainImage = service.images && service.images.length > 0 
                    ? service.images[service.mainImageIndex || 0] 
                    : null;
                  
                  return (
                    <div
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service);
                        updateFormData('serviceId', service.id);
                        updateFormData('serviceName', service.name);
                        updateFormData('duration', service.duration || 120);
                        updateFormData('totalAmount', service.price || 0);
                        setIsSelectingService(true); // Set flag to trigger auto-advance
                      }}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        formData.serviceId === service.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Service Image */}
                        {mainImage ? (
                          <img 
                            src={mainImage} 
                            alt={service.name}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-8 h-8 text-blue-600" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900">{service.name}</h4>
                          {service.description && (
                            <div className="mt-1">
                              <p className="text-sm text-gray-600">
                                {displayDescription}
                              </p>
                              {shouldTruncate && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleServiceDescription(service.id);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                >
                                  {isExpanded ? 'See less' : 'See more'}
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {service.duration || 120} min
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              Max {service.capacity || 12}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className="text-xl font-bold text-blue-600">
                            ${(service.price || 0).toFixed(2)}
                          </div>
                          {formData.serviceId === service.id && (
                            <Check className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </>
            )}
            
            {/* Date First Flow */}
            {entryPoint === 'date' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Select a Date</h4>
                <div>
                  <Label>Date <span className="text-red-500">*</span></Label>
                  <div 
                    onClick={() => document.getElementById('date-first-input')?.showPicker?.()}
                    className="mt-1 cursor-pointer"
                  >
                    <Input
                      id="date-first-input"
                      type="date"
                      value={formData.date}
                      onChange={(e) => updateFormData('date', e.target.value)}
                      className="text-base cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Once you pick a date, we'll show available services for that day
                  </p>
                </div>
                
                {/* Show services if date is selected */}
                {formData.date && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Available Services on {formData.date}</h4>
                    {loadingServices ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                      </div>
                    ) : services.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-600">No services available</p>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {services.filter(s => s.active).map((service) => (
                          <div
                            key={service.id}
                            onClick={() => {
                              setSelectedService(service);
                              updateFormData('serviceId', service.id);
                              updateFormData('serviceName', service.name);
                              updateFormData('duration', service.duration || 120);
                              updateFormData('totalAmount', service.price || 0);
                            }}
                            className={`p-4 border rounded-lg cursor-pointer transition-all ${
                              formData.serviceId === service.id
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{service.name}</h4>
                                {service.description && (
                                  <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {service.duration || 120} min
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    Max {service.capacity || 12}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <CreditCard className="w-4 h-4" />
                                    ${(service.price || 0).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              {formData.serviceId === service.id && (
                                <Check className="w-6 h-6 text-blue-600 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      
      case 2:
        // Date & Time - Multi-view flow like customer widget
        return (
          <div className="space-y-4">
            {/* Top navigation with back arrow */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  if (step2SubView === 'timeSlots') {
                    setStep2SubView('calendar');
                  } else {
                    setCurrentStep(1); // Back to services
                  }
                }}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {step2SubView === 'calendar' && 'Back to services'}
                {step2SubView === 'timeSlots' && 'Back to calendar'}
              </button>
              
              {/* Service name + booking type indicator */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                  (selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset')
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {(selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset') ? (
                    <>
                      <Package className="w-3 h-3" />
                      <span>Per-Asset</span>
                    </>
                  ) : (
                    <>
                      <Users className="w-3 h-3" />
                      <span>Per-Ticket</span>
                    </>
                  )}
                </div>
                <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200">
                  {selectedService?.name}
                </div>
              </div>
            </div>

            {/* Sub-View: Calendar */}
            {step2SubView === 'calendar' && (
              <ServiceCalendarPicker
                service={selectedService}
                selectedDate={formData.date}
                selectedTime={formData.startTime}
                onDateSelect={(date) => {
                  updateFormData('date', date);
                  updateFormData('startTime', '');
                  updateFormData('endTime', '');
                  updateFormData('groupSize', 1);
                  // Advance to time slots view
                  setStep2SubView('timeSlots');
                }}
                onTimeSelect={() => {}} // Not used in calendar view
                hideTimeSlots={true}
              />
            )}

            {/* Sub-View: Time Slots with inline duration/capacity */}
            {step2SubView === 'timeSlots' && formData.date && (
              <div className="space-y-4">
                {/* Date header with navigation */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const currentDate = new Date(formData.date + 'T00:00:00');
                      currentDate.setDate(currentDate.getDate() - 1);
                      const newDate = currentDate.toISOString().split('T')[0];
                      updateFormData('date', newDate);
                      updateFormData('startTime', '');
                      updateFormData('groupSize', 1);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold">
                    {new Date(formData.date + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      const currentDate = new Date(formData.date + 'T00:00:00');
                      currentDate.setDate(currentDate.getDate() + 1);
                      const newDate = currentDate.toISOString().split('T')[0];
                      updateFormData('date', newDate);
                      updateFormData('startTime', '');
                      updateFormData('groupSize', 1);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Available times */}
                {(() => {
                  const availableTimes = [];
                  if (selectedService?.availableTimeSlots) {
                    const date = new Date(formData.date + 'T00:00:00');
                    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                    const dayName = dayNames[date.getDay()];
                    
                    selectedService.availableTimeSlots.forEach(slot => {
                      if (slot.days && slot.days.map(d => d.toLowerCase()).includes(dayName)) {
                        availableTimes.push(slot.time);
                      }
                    });
                    availableTimes.sort();
                  }

                  const formatTime = (time24) => {
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}:${minutes} ${ampm}`;
                  };

                  const isPerAsset = selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset';
                  const numAssets = selectedService?.linkedAssets?.length || selectedService?.assetIds?.length || 1;
                  const assetCapacity = selectedService?.capacity || 12;
                  const totalCapacity = numAssets * assetCapacity;
                  
                  // Normalize duration options - handle both {hours: 1} and {duration: 60}
                  let durationOptions = (selectedService?.durationOptions || []).map(opt => ({
                    hours: opt.hours || (opt.duration ? opt.duration / 60 : 1),
                    price: opt.price || selectedService?.price || 0
                  }));
                  
                  const hasDurationOptions = durationOptions.length > 0;

                  return availableTimes.length > 0 ? (
                    <div className="space-y-3">
                      {/* Time slot buttons */}
                      <div className="grid grid-cols-2 gap-3">
                        {availableTimes.map(time => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => {
                              if (formData.startTime === time) {
                                // Deselect if clicking same time
                                updateFormData('startTime', '');
                                updateFormData('endTime', '');
                                updateFormData('groupSize', 1);
                              } else {
                                updateFormData('startTime', time);
                                // Auto-calculate end time
                                if (selectedService?.duration) {
                                  const [hours, minutes] = time.split(':').map(Number);
                                  const totalMinutes = hours * 60 + minutes + selectedService.duration;
                                  const endHours = Math.floor(totalMinutes / 60) % 24;
                                  const endMinutes = totalMinutes % 60;
                                  const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
                                  updateFormData('endTime', endTime);
                                }
                              }
                            }}
                            className={`
                              py-4 rounded-lg text-sm font-semibold transition-all
                              ${formData.startTime === time 
                                ? 'bg-blue-800 text-white' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'}
                            `}
                          >
                            {formatTime(time)} CST
                          </button>
                        ))}
                      </div>

                      {/* Duration/Capacity appears below selected time */}
                      {formData.startTime && (
                        <div className="pt-3 animate-in slide-in-from-top-2 duration-300">
                          {isPerAsset ? (
                            // Per-Asset: Show booking options
                            <div className="space-y-2">
                              {hasDurationOptions ? (
                                durationOptions.map((duration, idx) => {
                                  const availableCount = numAssets; // Number of assets available
                                  const selectedCount = formData.selectedDurationIndex === idx ? (formData.groupSize || 0) : 0;
                                  const remaining = availableCount - selectedCount;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className={`
                                        p-3 border rounded-lg transition-all
                                        ${formData.selectedDurationIndex === idx ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
                                      `}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-semibold text-gray-900">
                                            {duration.hours} {duration.hours === 1 ? 'Hour' : 'Hours'} - ${duration.price}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {remaining} available (up to {assetCapacity} guests per booking)
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (formData.selectedDurationIndex !== idx) {
                                                updateFormData('selectedDurationIndex', idx);
                                                updateFormData('duration', duration.hours * 60);
                                                updateFormData('groupSize', 1);
                                              } else {
                                                updateFormData('groupSize', Math.max(1, (formData.groupSize || 0) - 1));
                                              }
                                            }}
                                            className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                          >
                                            −
                                          </button>
                                          <span className="w-10 text-center font-bold text-lg">{selectedCount}</span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              updateFormData('selectedDurationIndex', idx);
                                              updateFormData('duration', duration.hours * 60);
                                              updateFormData('groupSize', Math.min(availableCount, (selectedCount || 0) + 1));
                                            }}
                                            className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                // Single duration
                                <div className="p-3 border border-gray-200 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-semibold text-gray-900">{selectedService.duration || 60} min</p>
                                      <p className="text-xs text-gray-500">
                                        {numAssets - (formData.groupSize || 0)} available (up to {assetCapacity} guests per booking)
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.max(1, (formData.groupSize || 1) - 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        −
                                      </button>
                                      <span className="w-10 text-center font-bold text-lg">{formData.groupSize || 0}</span>
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.min(numAssets, (formData.groupSize || 0) + 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            // Per-Ticket: Show ticket options with live countdown
                            <div className="space-y-2">
                              {hasDurationOptions ? (
                                durationOptions.map((duration, idx) => {
                                  const selectedCount = formData[`tickets_duration_${idx}`] || 0;
                                  const remaining = totalCapacity - selectedCount;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className={`
                                        p-3 border rounded-lg transition-all
                                        ${selectedCount > 0 ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
                                      `}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-semibold text-gray-900">
                                            {duration.hours} {duration.hours === 1 ? 'Hour' : 'Hours'} - ${duration.price}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {remaining} tickets available
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const current = formData[`tickets_duration_${idx}`] || 0;
                                              updateFormData(`tickets_duration_${idx}`, Math.max(0, current - 1));
                                              // Update groupSize to total
                                              const total = Object.keys(formData)
                                                .filter(k => k.startsWith('tickets_duration_'))
                                                .reduce((sum, k) => sum + (k === `tickets_duration_${idx}` ? Math.max(0, current - 1) : (formData[k] || 0)), 0);
                                              updateFormData('groupSize', total);
                                            }}
                                            className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                          >
                                            −
                                          </button>
                                          <span className="w-10 text-center font-bold text-lg">{selectedCount}</span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const current = formData[`tickets_duration_${idx}`] || 0;
                                              updateFormData(`tickets_duration_${idx}`, Math.min(totalCapacity, current + 1));
                                              updateFormData('selectedDurationIndex', idx);
                                              updateFormData('duration', duration.hours * 60);
                                              // Update groupSize to total
                                              const total = Object.keys(formData)
                                                .filter(k => k.startsWith('tickets_duration_'))
                                                .reduce((sum, k) => sum + (k === `tickets_duration_${idx}` ? Math.min(totalCapacity, current + 1) : (formData[k] || 0)), 0);
                                              updateFormData('groupSize', total);
                                            }}
                                            className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                // Single duration
                                <div className="p-3 border border-gray-200 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-semibold text-gray-900">{selectedService.duration || 60} min</p>
                                      <p className="text-xs text-gray-500">
                                        {totalCapacity - (formData.groupSize || 0)} tickets available
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.max(1, (formData.groupSize || 1) - 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        −
                                      </button>
                                      <span className="w-10 text-center font-bold text-lg">{formData.groupSize || 0}</span>
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.min(totalCapacity, (formData.groupSize || 0) + 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">No time slots for this date</p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );

      case 3:
        // Add-ons (optional, skip if no add-ons)
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Enhance Your Booking
            </h3>

            {selectedService?.addOns && selectedService.addOns.length > 0 ? (
              <div className="space-y-3">
                {selectedService.addOns.map((addon, index) => {
                  const addonKey = addon.id || `addon_${index}`;
                  const isSelected = formData.selectedAddOns[addonKey] !== undefined;
                  const addonPrice = addon.type === 'percentage'
                    ? (selectedService.price * (addon.percentage || 0) / 100)
                    : (addon.price || 0);

                  return (
                    <div
                      key={addonKey}
                      onClick={() => {
                        const newAddOns = { ...formData.selectedAddOns };
                        if (isSelected) {
                          delete newAddOns[addonKey];
                        } else {
                          newAddOns[addonKey] = addonPrice;
                        }
                        updateFormData('selectedAddOns', newAddOns);
                        
                        // Update total amount
                        const addOnsTotal = Object.values(newAddOns).reduce((sum, price) => sum + price, 0);
                        updateFormData('totalAmount', (selectedService.price || 0) + addOnsTotal);
                      }}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all
                        ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`
                            w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5
                            ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}
                          `}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {addon.title || addon.name}
                              {addon.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            {addon.description && (
                              <p className="text-sm text-gray-600 mt-1">{addon.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-blue-600">
                            {addonPrice > 0 ? `+$${addonPrice.toFixed(2)}` : 'Free'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">No add-ons available for this service</p>
              </div>
            )}
          </div>
        );

      case 4:
        // Customer Details - Simplified
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Details
            </h3>

            <div>
              <Label>Customer Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.customerName}
                onChange={(e) => updateFormData('customerName', e.target.value)}
                placeholder="John Doe"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Customer Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => updateFormData('customerEmail', e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Customer Phone <span className="text-red-500">*</span></Label>
              <Input
                type="tel"
                value={formData.customerPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  updateFormData('customerPhone', value);
                }}
                placeholder="5551234567"
                className="mt-1"
                maxLength={15}
                required
              />
              <p className="text-xs text-gray-400 mt-0.5">Numbers only</p>
            </div>

            <div>
              <Label>Pickup Location</Label>
              <Input
                value={formData.pickupLocation}
                onChange={(e) => updateFormData('pickupLocation', e.target.value)}
                placeholder="Enter pickup location"
                className="mt-1"
              />
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const defaultLocation = localStorage.getItem('defaultPickupLocation') || '';
                    if (defaultLocation) {
                      updateFormData('pickupLocation', defaultLocation);
                      toast.success('Default location applied');
                    } else {
                      toast.error('No default location saved yet');
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Use default location
                </button>
                {formData.pickupLocation && (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('defaultPickupLocation', formData.pickupLocation);
                      toast.success('Saved as default location');
                    }}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Save as default
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Don't worry - you can always change this on individual bookings later
              </p>
            </div>
          </div>
        );

      case 5:
        // Review & Confirm
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Review & Confirm
            </h3>

            {/* Pricing Breakdown */}
            {(() => {
              const basePrice = parseFloat(selectedService?.price || 0);
              const addOnsTotal = Object.values(formData.selectedAddOns).reduce((sum, price) => sum + price, 0);
              const preTotal = basePrice + addOnsTotal;
              const platformFee = (preTotal * 0.059) + 0.30;
              const salesTax = selectedService?.salesTaxEnabled ? preTotal * (selectedService?.salesTaxRate || 0) : 0;
              const grandTotal = preTotal + platformFee + salesTax;

              return (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Service Price:</span>
                    <span className="font-semibold">${basePrice.toFixed(2)}</span>
                  </div>
                  
                  {addOnsTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Add-ons:</span>
                      <span className="font-semibold">+${addOnsTotal.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-blue-200 pt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="font-semibold">${preTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Platform Fee:</span>
                    <span className="font-semibold">+${platformFee.toFixed(2)}</span>
                  </div>

                  {selectedService?.salesTaxEnabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Sales Tax ({(selectedService.salesTaxRate * 100).toFixed(2)}%):</span>
                      <span className="font-semibold">+${salesTax.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-blue-300 pt-2">
                    <div className="flex justify-between text-lg font-bold text-blue-700">
                      <span>Total Amount:</span>
                      <span>${grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Booking Summary */}
            <div className="space-y-3 text-sm">
              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                <p className="font-semibold text-gray-900">{selectedService?.name}</p>
              </div>

              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                <p className="font-semibold text-gray-900">
                  {new Date(formData.date + 'T00:00:00').toLocaleDateString('en-US', { 
                    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
                  })} at {formData.startTime}
                </p>
              </div>

              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                <p className="font-semibold text-gray-900">{formData.customerName}</p>
                <p className="text-xs text-gray-600">{formData.customerEmail}</p>
                <p className="text-xs text-gray-600">{formData.customerPhone}</p>
              </div>

              {formData.pickupLocation && (
                <div className="border-b pb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pickup Location</p>
                  <p className="font-semibold text-gray-900">{formData.pickupLocation}</p>
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        // Payment
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment
            </h3>

            {/* Payment Method Selector - 4 Clean Options */}
            <div className="grid gap-3">
              {/* Option 1: Pay Now (Redirect to Stripe) */}
              <div
                onClick={() => updateFormData('paymentStatus', 'pay_now_redirect')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'pay_now_redirect' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'pay_now_redirect' ? 'bg-blue-600' : 'bg-gray-100'}
                    `}>
                      <CreditCard className={`w-5 h-5 ${formData.paymentStatus === 'pay_now_redirect' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Pay Now</p>
                      <p className="text-xs text-gray-600">Enter card details via Stripe (over phone)</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'pay_now_redirect' && <Check className="w-5 h-5 text-blue-600" />}
                </div>
              </div>

              {/* Option 2: Share Payment Link */}
              <div
                onClick={() => updateFormData('paymentStatus', 'send_link')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'send_link' ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'send_link' ? 'bg-purple-600' : 'bg-gray-100'}
                    `}>
                      <Link2 className={`w-5 h-5 ${formData.paymentStatus === 'send_link' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Share Payment Link</p>
                      <p className="text-xs text-gray-600">Send Stripe link to customer via SMS/Email</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'send_link' && <Check className="w-5 h-5 text-purple-600" />}
                </div>
              </div>

              {/* Option 3: Already Paid */}
              <div
                onClick={() => updateFormData('paymentStatus', 'paid')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'paid' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'paid' ? 'bg-green-600' : 'bg-gray-100'}
                    `}>
                      <Check className={`w-5 h-5 ${formData.paymentStatus === 'paid' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Already Paid</p>
                      <p className="text-xs text-gray-600">Payment collected externally (cash, check, etc.)</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'paid' && <Check className="w-5 h-5 text-green-600" />}
                </div>
              </div>

              {/* Option 4: Will Pay Later */}
              <div
                onClick={() => updateFormData('paymentStatus', 'unpaid')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'unpaid' ? 'border-yellow-600 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'unpaid' ? 'bg-yellow-600' : 'bg-gray-100'}
                    `}>
                      <Clock className={`w-5 h-5 ${formData.paymentStatus === 'unpaid' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Will Pay Later</p>
                      <p className="text-xs text-gray-600">Deposit, on arrival, invoice, etc.</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'unpaid' && <Check className="w-5 h-5 text-yellow-600" />}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 0,
          padding: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onCancel();
          }
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
          style={{ margin: '16px' }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Add Booking</h2>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Continuous Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${(getStep2Progress() / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStepContent()}
        </div>

        {/* Footer Buttons */}
        <div className="p-6 border-t flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          {currentStep === totalSteps ? (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.paymentStatus}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2 text-white font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                <>
                  Confirm & Create Booking
                  <Check className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && createdBooking && (
        <>
          {/* Success modal content */}
          
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 0,
              padding: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={async () => {
              console.log('Success modal backdrop clicked');
              setShowSuccessModal(false);
              onCancel();
              if (window.refreshBookingData) {
                await window.refreshBookingData();
              }
            }}
          >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div className={`p-6 text-center ${
              createdBooking.paymentStatus === 'paid' 
                ? 'bg-gradient-to-r from-green-500 to-green-600' 
                : createdBooking.paymentStatus === 'pending'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600'
                : 'bg-gradient-to-r from-yellow-500 to-yellow-600'
            }`}>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className={`w-10 h-10 ${
                  createdBooking.paymentStatus === 'paid' ? 'text-green-600' :
                  createdBooking.paymentStatus === 'pending' ? 'text-blue-600' :
                  'text-yellow-600'
                }`} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {createdBooking.paymentStatus === 'paid' ? 'Booking Confirmed!' :
                 createdBooking.paymentStatus === 'pending' ? 'Payment Link Created!' :
                 'Booking Created!'}
              </h2>
              <p className="text-white text-opacity-90">
                {createdBooking.paymentStatus === 'paid' ? 'Payment received - Customer notified' :
                 createdBooking.paymentStatus === 'pending' ? 'Awaiting customer payment' :
                 createdBooking.paymentStatus === 'unpaid' ? 'Payment to be collected later' :
                 'Customer added to Relations'}
              </p>
            </div>

            {/* Booking Details */}
            <div className="p-6 space-y-4">
              {/* Confirmation Code */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Confirmation Code</p>
                <p className="text-3xl font-mono font-bold text-blue-600">{createdBooking.confirmationCode}</p>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="text-sm font-semibold text-gray-900">{createdBooking.customerName}</p>
                  {createdBooking.customerEmail && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" />
                      {createdBooking.customerEmail}
                    </p>
                  )}
                  {createdBooking.customerPhone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />
                      {createdBooking.customerPhone}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {(() => {
                      // Use preferredDate (not date) and handle undefined
                      const dateStr = createdBooking.preferredDate || createdBooking.date;
                      if (!dateStr) return 'Date not available';
                      
                      const [year, month, day] = dateStr.split('-').map(Number);
                      const dateObj = new Date(year, month - 1, day);
                      return dateObj.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                    })()}
                  </p>
                  <p className="text-sm text-gray-900">
                    {createdBooking.preferredTime || createdBooking.startTime} <span className="text-gray-500">{getTimezoneAbbreviation(createdBooking.timezone || orgTimezone)}</span>
                  </p>
                  <p className="text-xs text-gray-600">{createdBooking.duration || 60} minutes</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Capacity</p>
                  <p className="text-sm font-semibold text-gray-900">{createdBooking.groupSize} {createdBooking.groupSize === 1 ? 'person' : 'people'}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {createdBooking.asset?.name || <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Team</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {createdBooking.teamAssignments?.length > 0 
                      ? `${createdBooking.teamAssignments.length} assigned` 
                      : <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">{createdBooking.paymentStatus.replace('_', ' ')}</p>
                  {createdBooking.totalAmount > 0 && (() => {
                    const orgAmount = parseFloat(createdBooking.totalAmount);
                    const platformFee = orgAmount * 0.03;
                    const stripeFee = (orgAmount * 0.029) + 0.30;
                    const combinedFees = platformFee + stripeFee;
                    const customerPays = orgAmount + combinedFees;
                    
                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-600">Amount: ${orgAmount.toFixed(2)}</p>
                        <p className="text-xs text-gray-600">Processing & Platform Fees: +${combinedFees.toFixed(2)}</p>
                        <p className="text-sm font-bold text-blue-600">Customer Charged: ${customerPays.toFixed(2)}</p>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Action Buttons - Dynamic based on contact info */}
              <div className="space-y-2 pt-4">
                {/* Always show Copy Link */}
                <Button
                  variant="outline"
                  onClick={() => {
                    const link = `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                    navigator.clipboard.writeText(link);
                    toast.success('Ticket link copied!');
                  }}
                  className="w-full"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Ticket Link
                </Button>

                {/* Add to Calendar Button */}
                <Button
                  variant="outline"
                  onClick={() => {
                    // Use webcal for automatic calendar subscription
                    const baseUrl = BACKEND_URL.replace('http://', '').replace('https://', '');
                    const calendarUrl = `webcal://${baseUrl}/api/customer/${createdBooking.confirmationCode}/calendar-feed.ics`;
                    window.location.href = calendarUrl;
                    toast.success('Opening calendar app to sync your booking...');
                  }}
                  className="w-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Sync to Calendar
                </Button>

                {/* Show email/sms buttons if contact info provided */}
                {(createdBooking.customerEmail || createdBooking.customerPhone) && (
                  <div className="grid grid-cols-2 gap-2">
                    {createdBooking.customerEmail && (
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            await api.post('/emails/send-to-customer', {
                              email: createdBooking.customerEmail,
                              name: createdBooking.customerName,
                              subject: `Your Booking Confirmation - ${createdBooking.confirmationCode}`,
                              message: `Hi ${createdBooking.customerName},\n\nYour booking is confirmed!\n\nConfirmation Code: ${createdBooking.confirmationCode}\nDate: ${new Date(createdBooking.date).toLocaleDateString()}\nTime: ${createdBooking.startTime} ${getTimezoneAbbreviation(orgTimezone)}\n\nView your ticket: ${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}\n\nSee you soon!`
                            });
                            toast.success('Email sent!');
                          } catch (error) {
                            toast.error('Failed to send email');
                          }
                        }}
                        className="flex-1"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    )}
                    {createdBooking.customerPhone && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          toast.info('SMS feature coming soon!');
                        }}
                        className="flex-1"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Text
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={async () => {
                  console.log('Done button clicked - closing modals and refreshing data');
                  
                  setShowSuccessModal(false);
                  onCancel(); // Close AddBookingWizard
                  
                  // Trigger data refresh in parent CalendarScheduler
                  if (window.refreshBookingData) {
                    await window.refreshBookingData();
                  }
                  
                  toast.success('Booking created successfully!');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Share Payment Link Modal */}
      {showShareLinkModal && paymentLinkData && (
        <SharePaymentLinkModal
          open={showShareLinkModal}
          onClose={() => {
            setShowShareLinkModal(false);
            onComplete(); // Close wizard and refresh bookings
          }}
          checkoutUrl={paymentLinkData.checkoutUrl}
          customerEmail={paymentLinkData.customerEmail}
          customerPhone={paymentLinkData.customerPhone}
          bookingDetails={paymentLinkData.bookingDetails}
        />
      )}
    </div>
    </>
  );
};

export default AddBookingWizard;
